import React, { useState, useEffect } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatMoney, formatDate } from "../utils/utils";

const CobranzaAdelantoModal = ({ visible, onClose, pacienteID, pacienteNombre, periodo, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [fechaInicio, setFechaInicio] = useState("");
    const [fechaFin, setFechaFin] = useState("");
    const [observacion, setObservacion] = useState("");
    const [historialAdelantos, setHistorialAdelantos] = useState([]);

    useEffect(() => {
        if (visible && pacienteID && periodo) {
            setFechaInicio(`${periodo}-26`);
            setFechaFin("");
            setObservacion("");
            fetchAdelantos();
        }
    }, [visible, pacienteID, periodo]);

    const fetchAdelantos = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.post("/acuerdo-mutuo/cobranza/adelanto/lista", {
                periodo: periodo,
                paciente_id: pacienteID // We can send paciente_id filtering or filter it in frontend
            });
            if (!response.data.error) {
                // If the backend returns all for the period, filter by patient. If it filters by patient, just set.
                // Assuming it filters by patient if we send it, or we filter locally.
                const allAdelantos = response.data.data || [];
                const patientAdelantos = allAdelantos.filter(a => Number(a.paciente_id) === Number(pacienteID));
                setHistorialAdelantos(patientAdelantos);
            }
        } catch (error) {
            console.error("Error al obtener adelantos:", error);
            // Optionally set mock data if backend isn't ready
        } finally {
            setLoading(false);
        }
    };

    if (!visible) return null;

    const handleRegistrarAdelanto = async () => {
        if (!fechaInicio || !fechaFin) {
            alert("Debe seleccionar una fecha de inicio y una fecha de fin.");
            return;
        }
        if (new Date(fechaInicio) > new Date(fechaFin)) {
            alert("La fecha de inicio no puede ser posterior a la fecha de fin.");
            return;
        }

        try {
            setLoading(true);

            // Real call
            await axiosInstance.post("/acuerdo-mutuo/cobranza/adelanto", {
                paciente_id: pacienteID,
                periodo: periodo,
                fecha_inicio: fechaInicio,
                fecha_fin: fechaFin,
                observacion: observacion
            });

            alert("Adelanto configurado correctamente.");
            
            setFechaInicio(`${periodo}-26`);
            setFechaFin("");
            setObservacion("");
            fetchAdelantos();
            
            if (onSuccess) onSuccess(); // To reload the main table
        } catch (error) {
            console.error("Error al registrar adelanto:", error);
            alert("Ocurrió un error al configurar el adelanto.");
        } finally {
            setLoading(false);
        }
    };

    const handleEliminarAdelanto = async (id) => {
        if (!window.confirm("¿Está seguro de que desea eliminar este adelanto? Los montos se restarán de la cobranza actual.")) return;
        
        try {
            setLoading(true);
            await axiosInstance.post("/acuerdo-mutuo/cobranza/adelanto/eliminar", { id });
            alert("Adelanto eliminado correctamente.");
            fetchAdelantos();
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error("Error al eliminar adelanto:", error);
            alert("Error al eliminar el adelanto.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", overflowY: "auto" }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header bg-primary text-white border-0">
                        <h5 className="modal-title">
                            <i className="bi bi-calendar-check me-2"></i> Adelantos de Turno (Siguiente Periodo)
                        </h5>
                        <button className="btn-close btn-close-white" onClick={onClose} disabled={loading}></button>
                    </div>

                    <div className="modal-body bg-light">
                        <div className="mb-4 text-center">
                            <h4 className="text-dark fw-bold mb-1">{pacienteNombre}</h4>
                            <span className="badge bg-secondary fs-6">Periodo Actual (Cobro): {periodo}</span>
                            <p className="text-muted small mt-2">
                                Configure fechas del <strong>próximo mes</strong> que desea cobrar por adelantado en el periodo actual.
                            </p>
                        </div>

                        {/* FORMULARIO DE ADELANTO */}
                        <div className="card shadow-sm border-0 mb-4">
                            <div className="card-body">
                                <h6 className="card-title text-primary fw-bold mb-3 border-bottom pb-2">Registrar Nuevo Adelanto</h6>
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label text-muted small fw-bold">Fecha Inicio</label>
                                        <input 
                                            type="date" 
                                            className="form-control form-control-sm"
                                            value={fechaInicio}
                                            disabled={true} // Siempre deshabilitado porque es fijo el 26
                                            title="La fecha de inicio siempre empieza el día 26 del periodo actual"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label text-muted small fw-bold">Fecha Fin</label>
                                        <input 
                                            type="date" 
                                            className="form-control form-control-sm"
                                            value={fechaFin}
                                            onChange={(e) => setFechaFin(e.target.value)}
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="col-md-12">
                                        <label className="form-label text-muted small fw-bold">Observación (Opcional)</label>
                                        <input 
                                            type="text" 
                                            className="form-control form-control-sm"
                                            placeholder="Ej. Adelanto 5 días de junio"
                                            value={observacion}
                                            onChange={(e) => setObservacion(e.target.value)}
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="col-12 text-end">
                                        <button 
                                            className="btn btn-sm btn-primary px-4 fw-bold shadow-sm"
                                            onClick={handleRegistrarAdelanto}
                                            disabled={loading || !fechaInicio || !fechaFin}
                                        >
                                            {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-plus-circle me-1"></i>}
                                            Guardar Adelanto
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* HISTORIAL DE ADELANTOS */}
                        <div className="card shadow-sm border-0">
                            <div className="card-body p-0">
                                <h6 className="card-title text-dark fw-bold m-3 mb-2">Adelantos Activos configurados para {periodo}</h6>
                                <div className="table-responsive">
                                    <table className="table table-hover table-sm align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="ps-3 text-muted small">ID</th>
                                                <th className="text-muted small">Rango Fechas Adelantadas</th>
                                                <th className="text-muted small">Observación</th>
                                                <th className="text-end pe-3 text-muted small">Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historialAdelantos.length === 0 ? (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-4 text-muted fst-italic">
                                                        No hay adelantos configurados para este paciente en el periodo.
                                                    </td>
                                                </tr>
                                            ) : (
                                                historialAdelantos.map((ad) => (
                                                    <tr key={ad.id}>
                                                        <td className="ps-3 fw-bold text-secondary">#{ad.id}</td>
                                                        <td>
                                                            <span className="badge bg-info text-dark">
                                                                <i className="bi bi-calendar me-1"></i>
                                                                {formatDate(ad.fecha_inicio)} AL {formatDate(ad.fecha_fin)}
                                                            </span>
                                                        </td>
                                                        <td className="text-muted small">
                                                            {ad.observacion || "-"}
                                                        </td>
                                                        <td className="text-end pe-3">
                                                            <button 
                                                                className="btn btn-sm btn-outline-danger shadow-sm"
                                                                onClick={() => handleEliminarAdelanto(ad.id)}
                                                                disabled={loading}
                                                                title="Eliminar este rango de adelanto"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className="modal-footer border-0 bg-light">
                        <button className="btn btn-secondary px-4 fw-bold" onClick={onClose} disabled={loading}>Cerrar</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CobranzaAdelantoModal;
